
import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { SubscriptionData, SubscriptionTier } from "@/types/auth";
import { Session } from "@supabase/supabase-js";

export function useSubscriptionData() {
  const [subscriptionData, setSubscriptionData] = useState<SubscriptionData>({
    subscribed: false,
    subscription_tier: null,
    subscription_end: null,
    subscription_id: null,
    webhook_secret: null,
    is_owner: false,
  });

  const refreshSubscription = async (session?: Session | null) => {
    try {
      // Se não tiver sessão, resetar dados
      if (!session?.user) {
        setSubscriptionData({
          subscribed: false,
          subscription_tier: null,
          subscription_end: null,
          subscription_id: null,
          webhook_secret: null,
          is_owner: false,
        });
        return;
      }

      const currentUser = session.user;

      try {
        // PASSO 1: Buscar subscription_id do perfil do usuário
        const { data: profileData, error: profileError } = await supabase
          .from('profiles')
          .select('subscription_id')
          .eq('id', currentUser.id)
          .maybeSingle();

        if (profileError) {
          setSubscriptionData({
            subscribed: false,
            subscription_tier: null,
            subscription_end: null,
            subscription_id: null,
            webhook_secret: null,
            is_owner: false,
          });
          return;
        }

        const profileSubscriptionId = profileData?.subscription_id;

        // Se não há subscription_id no perfil, usuário não tem subscription
        if (!profileSubscriptionId) {
          setSubscriptionData({
            subscribed: false,
            subscription_tier: null,
            subscription_end: null,
            subscription_id: null,
            webhook_secret: null,
            is_owner: false,
          });
          return;
        }

        // PASSO 2: Buscar dados da subscription usando o subscription_id
        const { data: subscriptionData, error: subscriptionError } = await supabase
          .from('subscribers')
          .select('id, user_id, subscribed, subscription_tier, subscription_end, webhook_secret')
          .eq('id', profileSubscriptionId)
          .maybeSingle();

        if (subscriptionError) {
          console.error('Error fetching subscription data:', subscriptionError);
          setSubscriptionData({
            subscribed: false,
            subscription_tier: null,
            subscription_end: null,
            subscription_id: null,
            webhook_secret: null,
            is_owner: false,
          });
          return;
        }

        if (!subscriptionData) {
          setSubscriptionData({
            subscribed: false,
            subscription_tier: null,
            subscription_end: null,
            subscription_id: null,
            webhook_secret: null,
            is_owner: false,
          });
          return;
        }

        // PASSO 3: Determinar se o usuário é proprietário ou convidado
        const isOwner = subscriptionData.user_id === currentUser.id;

        // Validar e converter subscription_tier do banco para o tipo correto
        const validateSubscriptionTier = (tier: string | null): SubscriptionTier | null => {
          if (!tier) return null;
          if (tier === 'FREE' || tier === 'PRO' || tier === 'ENTERPRISE') {
            return tier as SubscriptionTier;
          }
          return 'FREE'; // Fallback para FREE se o valor for inválido
        };

        // Atualizar estado com dados da assinatura existente
        const finalSubscriptionData: SubscriptionData = {
          subscribed: subscriptionData?.subscribed || false,
          subscription_tier: validateSubscriptionTier(subscriptionData?.subscription_tier || null),
          subscription_end: subscriptionData?.subscription_end || null,
          subscription_id: subscriptionData?.id || null,
          webhook_secret: subscriptionData?.webhook_secret || null,
          is_owner: isOwner,
        };

        setSubscriptionData(finalSubscriptionData);
        
      } catch (queryError) {
        console.error('Query error:', queryError);
        setSubscriptionData({
          subscribed: false,
          subscription_tier: null,
          subscription_end: null,
          subscription_id: null,
          webhook_secret: null,
          is_owner: false,
        });
      }
    } catch (error: any) {
      console.error('General error in refreshSubscription:', error);
      setSubscriptionData({
        subscribed: false,
        subscription_tier: null,
        subscription_end: null,
        subscription_id: null,
        webhook_secret: null,
        is_owner: false,
      });
    }
  };

  const generateWebhookSecret = (): string => {
    // Usa crypto.randomUUID() se disponível, senão gera um GUID manualmente
    if (typeof crypto !== 'undefined' && crypto.randomUUID) {
      return crypto.randomUUID();
    } else {
      // Fallback para ambientes que não suportam crypto.randomUUID()
      return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
        const r = Math.random() * 16 | 0;
        const v = c === 'x' ? r : (r & 0x3 | 0x8);
        return v.toString(16);
      });
    }
  };

  const updateWebhookSecret = async (newSecret: string, currentSubscriptionId?: string): Promise<boolean> => {
    try {
      // Usar o subscription_id passado como parâmetro ou o do estado local
      const subscriptionIdToUse = currentSubscriptionId || subscriptionData.subscription_id;
      
      if (!subscriptionIdToUse) {
        console.error('Não há subscription_id para atualizar');
        return false;
      }

      const { error } = await supabase
        .from('subscribers')
        .update({ webhook_secret: newSecret })
        .eq('id', subscriptionIdToUse);

      if (error) {
        console.error('Erro ao atualizar webhook_secret:', error);
        return false;
      }

      // Atualizar o estado local apenas se a gravação foi bem-sucedida
      setSubscriptionData(prev => ({
        ...prev,
        webhook_secret: newSecret
      }));

      return true;
    } catch (error) {
      console.error('Erro ao atualizar webhook_secret:', error);
      return false;
    }
  };

  return {
    subscriptionData,
    setSubscriptionData,
    refreshSubscription,
    generateWebhookSecret,
    updateWebhookSecret
  };
}
